<template>
  <a-layout class="user-balance-movements-list">
    <TitleBreadcrumb />
    <div class="content-base container">
      <a-card class="user-balance-movements-list-card">
        <a-row
          type="flex"
          justify="end"
          :class="{ 'flex-direction-reverse': isMobile }"
          :gutter="[16, 16]"
        >
          <a-col :span="24">
            <a-input-search
              :placeholder="labels.placeholderSearch"
              style="width: 100%"
              @search="handleSearch"
              allowClear
            />
          </a-col>
        </a-row>
        <a-divider
          orientation="center"
          type="horizontal"
          :style="{ marginBottom: 0 }"
        />
        <a-row type="flex" justify="center" :gutter="[16, 16]">
          <a-col :xs="24">
            <StandardTable
              class="table-base"
              ref="standardTable"
              :fetchService="fetchClientUserBalanceMovements"
              :columns="columns"
              :locale="locale"
              :handleShowTotal="handleShowTotal"
              :scroll="{ x: isMobile ? true : false }"
              :rowKey="'number'"
              :paginationType="isMobile ? 'arrows' : 'numbers'"
              :customRow="handleCustomRow"
            />
          </a-col>
        </a-row>
      </a-card>
    </div>
  </a-layout>
</template>
<script>
import { mapActions, mapState } from "vuex";
import accounting from "accounting";
import TitleBreadcrumb from "@/components/layout/client/TitleBreadcrumb.vue";
import loadingMixin from "@/mixins/loadingMixin";
import { pageTitle } from "@/utils/utils";
import labels from "@/utils/labels";
import StandardTable from "@/components/core/table/StandardTable.vue";
import { DATE_FORMAT } from "../../utils/utils";
export default {
  components: { TitleBreadcrumb, StandardTable },
  name: "UserbalancemovementList",
  mixins: [loadingMixin],
  metaInfo: {
    title: pageTitle(labels.userbalancemovementList.subtitle),
    meta: [
      {
        name: "title",
        content: labels.appName,
      },
      {
        name: "description",
        content: labels.userbalancemovementList.subtitle,
      },
    ],
  },
  data() {
    return {
      labels: labels.userbalancemovementList,
      search: "",
      activeRow: null,
    };
  },
  methods: {
    ...mapActions("users", ["fetchClientUserBalanceMovements"]),
    handleReloadTable() {
      let filtersTable = this.$refs.standardTable.filters;
      let result = { ...filtersTable };
      this.$refs.standardTable.handleFilters(result);
    },
    handleShowTotal(total, range) {
      return `${range[0]}-${range[1]} de ${total}`;
    },
    handleSearch(search) {
      this.$refs.standardTable.handleSearch(search);
    },
    handleCustomRow(_, index) {
      return {
        on: {
          click: () => {
            this.activeRow = this.activeRow === index ? null : index;
          },
        },
      };
    },
    handleShowMoreInfo(record, event) {
      event.stopPropagation();
      if (record?.code) {
        this.$info({
          title: this.labels.moreInfo.title.replace(
            "{code}",
            record?.code || "-"
          ),
          class: "more-info-modal",
          content: (
            <div>
              <p>
                <strong>{this.labels.moreInfo.date}:</strong>{" "}
                {this.$moment(record?.date).format(
                  DATE_FORMAT.MOMENT_DATE_TIME
                )}
              </p>
              <p>
                <strong>{this.labels.moreInfo.code}:</strong>{" "}
                {record?.code || "-"}
              </p>
              <p>
                <strong>{this.labels.moreInfo.reference}:</strong>{" "}
                {record?.ticket?.number || record?.reference || "-"}
              </p>
              <p>
                <strong>{this.labels.moreInfo.type}:</strong>{" "}
                {record?.type?.name || "-"}
              </p>
              <p>
                <strong>{this.labels.moreInfo.origin}:</strong>{" "}
                {record?.origin?.name || "-"}
              </p>
              <p>
                <strong>{this.labels.moreInfo.amount}:</strong>{" "}
                {accounting.formatNumber(record?.amount, 2, ",", ".")}
              </p>
              <p>
                <strong>{this.labels.moreInfo.status}:</strong>{" "}
                {this.statusText(record?.status?.toLowerCase())}
              </p>
            </div>
          ),
        });
      }
    },
  },
  computed: {
    ...mapState("layout", ["screenWidth"]),
    locale() {
      return {
        emptyText: this.labels.table.emptyText,
      };
    },
    columns() {
      return [
        {
          title: this.labels.table.columns.date,
          dataIndex: "date",
          key: "date",
          sorter: true,
          hidden: this.isMobile,
          customRender: (text) => {
            return this.$moment(text).format(DATE_FORMAT.MOMENT_DATE_TIME);
          },
        },
        {
          title: this.labels.table.columns.number,
          dataIndex: "number",
          key: "number",
          sorter: true,
          hidden: this.isMobile,
        },
        {
          title: this.labels.table.columns.reference,
          dataIndex: "reference",
          key: "reference",
          sorter: true,
          hidden: this.isMobile,
        },
        {
          title: this.labels.table.columns.description,
          dataIndex: "description",
          key: "description",
          sorter: true,
          hidden: this.isMobile,
        },
        {
          title: this.labels.table.columns.debit,
          dataIndex: "amount",
          key: "debit-amount",
          sorter: true,
          hidden: this.isMobile,
          customRender: (text, record) => {
            return record?.type === "DEBIT"
              ? accounting.formatNumber(text || 0, 2, ",", ".")
              : "";
          },
        },
        {
          title: this.labels.table.columns.credit,
          dataIndex: "amount",
          key: "credit-amount",
          sorter: true,
          hidden: this.isMobile,
          customRender: (text, record) => {
            return record?.type === "CREDIT"
              ? accounting.formatNumber(text || 0, 2, ",", ".")
              : "";
          },
        },
        {
          title: this.labels.table.columns.balance,
          dataIndex: "new_balance",
          key: "new_balance",
          sorter: true,
          hidden: this.isMobile,
          customRender: (text) => {
            return accounting.formatNumber(text || 0, 2, ",", ".");
          },
        },
        {
          title: this.labels.table.columns.movements,
          dataIndex: "movements",
          key: "movements",
          sorter: false,
          hidden: !this.isMobile,
          customRender: (_, record) => {
            // Card item design
            const color = record?.type === "DEBIT" ? "#faad14" : "#52c41a";
            const type = record?.type
              ? this.labels.table.columns.type[record?.type?.toUpperCase()]
              : "-";
            return (
              <div class="list-item">
                <div class="list-item-head">
                  <div
                    class="list-item-head-header"
                    style={{ borderColor: color }}
                  >
                    <span>
                      {this.$options.filters.ucwords(
                        this.$moment(record.date).format(
                          DATE_FORMAT.MOMENT_DAY_MONTH
                        )
                      )}
                    </span>
                    <small>
                      {this.$moment(record.date)
                        .format(DATE_FORMAT.MOMENT_TIME)
                        .toUpperCase()}
                    </small>
                  </div>
                  <div class="list-item-head-content">
                    <div class="list-item-head-content-number">
                      <span>{record.number}</span>
                    </div>
                    <div class="list-item-head-content-type">
                      <span>{type}</span>
                    </div>
                    <div class="list-item-head-content-reference">
                      <span>
                        {record.reference}
                        <a-tooltip
                          placement="top"
                          title={record.description}
                          trigger="click"
                          class="list-item-head-content-reference-tooltip"
                        >
                          <a-icon
                            class="list-item-head-content-reference-tooltip-icon"
                            type="info-circle"
                            style={{ color: "#faad14" }}
                          />
                        </a-tooltip>
                      </span>
                    </div>
                  </div>
                  <div class="list-item-head-footer">
                    <div class="list-item-head-footer-amount">
                      <span>
                        {accounting.formatNumber(record.amount, 2, ",", ".")}
                      </span>
                    </div>
                    <div class="list-item-head-footer-balance">
                      <span>
                        {accounting.formatNumber(
                          record.new_balance,
                          2,
                          ",",
                          "."
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            );
          },
        },
      ].filter((column) => !column?.hidden);
    },
  },
};
</script>
<style lang="scss">
@import "~@/assets/scss/variable";
.user-balance-movements-list {
  max-width: 100% !important;
  font-family: "Roboto", sans-serif;
  background-color: transparent;
  @media screen and (max-width: $screen-mobile) {
    max-width: 100% !important;
  }
  .content-base {
    padding-top: 60px;
    @media screen and (max-width: $screen-mobile) {
      max-width: 100% !important;
      padding-top: 14px;
    }
  }
  &-card {
    background-color: var(--card-shop-bg);
    border-color: var(--card-shop-shadow);
    padding: 1.125rem;
    border-radius: 8px;
    @media screen and (max-width: $screen-mobile) {
      padding: 2.125rem 1rem;
      width: 100%;
    }
    .ant-input {
      box-shadow: 0 1px 4px var(--input-bg) !important;
      border: 1px solid var(--text-active) !important;
      color: var(--text) !important;
      font-family: "Exo 2", sans-serif !important;
      background-color: var(--input-bg) !important;
    }
    .ant-input-search-icon {
      color: var(--text) !important;
      transition: all 0.5s ease;
      :hover {
        color: var(--text-active) !important;
        transition: all 0.5s ease;
      }
    }
    .ant-input-clear-icon {
      color: var(--text) !important;
      transition: all 0.5s ease;
      :hover {
        color: var(--danger) !important;
        transition: all 0.5s ease;
      }
    }
    .ant-table {
      &:hover {
        transition: 0.5s ease;
        background: unset;
      }
      tr.ant-table-row:hover {
        transition: 0.5s ease;
        background: var(--table-hover) !important;
      }
      tr:nth-child(2n) {
        transition: 0.5s ease;
        background: var(--table-striped) !important;
      }
      tr.table-row-disabled,
      tr.table-row-disabled:hover {
        color: var(--table-disabled) !important;
        background: var(--table-disabled) !important;
        font-weight: 600;
      }
      .ant-table-tbody > tr.ant-table-row:hover > td {
        transition: 0.5s ease;
        background: none !important;
      }
      &-tbody,
      &-thead {
        color: var(--text) !important;
        tr {
          th {
            color: var(--text) !important;
            background-color: transparent !important;
          }
        }
      }
      &-footer {
        background: transparent;
      }
      .action-button-view {
        color: var(--btn-text-secondary);
        border-color: var(--btn-bc-secondary);
        background-color: var(--btn-bg-secondary);
        transition: all 0.5s ease;
        &:hover {
          transition: all 0.5s ease;
          color: var(--btn-text-secondary);
          border-color: var(--btn-bc-secondary);
          background-color: var(--btn-bg-secondary);
        }
      }
      .ant-table-placeholder {
        background: var(--card-bg);
        color: var(--text);
        border-radius: 0;
      }
      @media screen and (max-width: $screen-mobile) {
        &-small {
          border: none;
        }
        &-body {
          margin: 0 1px;
        }
      }
    }
    .ant-pagination-prev .ant-pagination-item-link,
    .ant-pagination-next .ant-pagination-item-link {
      color: var(--btn-text-secondary) !important;
      background-color: var(--btn-bg-secondary) !important;
      border-color: var(--btn-bc-secondary) !important;
    }
    .ant-table-pagination.mini {
      .ant-pagination-prev .ant-pagination-item-link,
      .ant-pagination-next .ant-pagination-item-link {
        background-color: transparent !important;
        border-color: none !important;
        color: var(--text) !important;
      }
    }
    .ant-pagination-item {
      color: var(--btn-text-secondary) !important;
      background-color: var(--btn-bg-secondary) !important;
      border-color: var(--btn-bc-secondary) !important;
      a {
        color: var(--btn-text-secondary) !important;
      }
      &-active {
        color: var(--btn-text-primary) !important;
        background-color: var(--btn-bg-primary) !important;
        border-color: var(--btn-bc-primary) !important;
        a {
          color: var(--btn-text-primary) !important;
        }
      }
    }
    .ant-pagination {
      color: var(--text) !important;
    }
    .flex-direction-reverse {
      flex-direction: column-reverse;
    }
  }
  .text-danger {
    color: var(--danger) !important;
  }
  .list-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
    &-head {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      gap: 6px;
      &-header {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        gap: 2px;
        max-width: 22%;
        border-right: 4px solid var(--text-muted);
        .success {
          color: var(--success);
        }
        .warning {
          color: var(--warning);
        }
        .danger {
          color: var(--danger);
        }
        span {
          font-size: 14px;
          font-weight: 600;
        }
        small {
          font-size: 12px;
          font-weight: 400;
          padding-right: 4px;
        }
      }
      &-content {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: flex-start;
        gap: 2px;
        max-width: 53%;
        overflow: hidden;
        &-number {
          span {
            font-size: 14px;
            font-weight: 600;
            text-overflow: ellipsis;
          }
        }
        &-type {
          span {
            font-size: 12px;
            font-weight: 400;
            text-overflow: ellipsis;
          }
        }
        &-reference {
          span {
            font-size: 14px;
            font-weight: 400;
            text-overflow: ellipsis;
          }
          &-tooltip {
            margin-left: 6px;
            .anticon {
              font-size: 18px;
            }
          }
        }
      }
      &-footer {
        max-width: 25%;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        gap: 2px;
        &-amount {
          span {
            font-size: 14px;
            font-weight: 600;
          }
        }
        &-balance {
          span {
            font-size: 12px;
            font-weight: 400;
          }
        }
      }
    }
  }
}
</style>
